import { Box, Center, Loader, Paper, Stack, Text } from "@mantine/core";
import AppPaginationBar from "@/components/shared/AppPaginationBar";
import { CollaborationStatus } from "@/components/shared/constants/general.const";
import InterestRequestsTable from "@/pages/interests/components/InterestRequestsTable";

const EMPTY_PANEL_STYLE = {
  borderColor: "#04305c",
  backgroundColor: "#ebf0ff",
  boxShadow: "0 1px 3px rgba(15, 23, 42, 0.08)",
} as const;

export function filterVisibleInterestRequests(requests: unknown[] | undefined, isRequester: boolean) {
  return (requests ?? []).filter((r: any) => {
    const s = String(r?.status ?? "").trim();
    if (s === CollaborationStatus.REQUESTED) return true;
    if (isRequester && s === CollaborationStatus.RECEIVER_DECLINED) return true;
    return false;
  });
}

export interface InterestRequestsPanelProps {
  loading: boolean;
  requests: any[];
  isRequester: boolean;
  totalPages: number;
  totalCount: number;
  page: number;
  onPageChange: (p: number) => void;
  emptyLabel: string;
  actionLoading: string | null;
  onAccept: (matchId: string) => void;
  onDecline: (matchId: string) => void;
  onMakePayment: (matchId: string) => void;
  onViewProfile: (companyId: string) => void;
  onOpenWorkspace: (matchId: string) => void;
}

export default function InterestRequestsPanel({
  loading,
  requests,
  isRequester,
  totalPages,
  totalCount,
  page,
  onPageChange,
  emptyLabel,
  actionLoading,
  onAccept,
  onDecline,
  onMakePayment,
  onViewProfile,
  onOpenWorkspace,
}: InterestRequestsPanelProps) {
  const visibleRequests = filterVisibleInterestRequests(requests ?? [], isRequester);

  const effectiveEmptyLabel =
    visibleRequests.length === 0 && (requests?.length ?? 0) > 0
      ? "No pending requests — accepted items are now in Collaborations."
      : emptyLabel;

  if (loading) {
    return (
      <Center py="xl">
        <Loader size="lg" />
      </Center>
    );
  }

  return (
    <Stack gap="md">
      {visibleRequests.length === 0 ? (
        <Paper p="xl" radius="lg" withBorder style={EMPTY_PANEL_STYLE}>
          <Text c="gray.5" ta="center">
            {effectiveEmptyLabel}
          </Text>
        </Paper>
      ) : (
        <InterestRequestsTable
          requests={visibleRequests}
          isRequester={isRequester}
          isLoading={false}
          actionLoading={actionLoading}
          onAccept={onAccept}
          onDecline={onDecline}
          onMakePayment={onMakePayment}
          onViewProfile={onViewProfile}
          onOpenWorkspace={onOpenWorkspace}
        />
      )}

      {totalPages > 1 && (
        <Box mt="xl">
          <AppPaginationBar
            page={page}
            totalPages={totalPages}
            totalItems={totalCount}
            pageSize={10}
            onPageChange={onPageChange}
            pageSizeOptions={[10]}
            boundaries={3}
          />
        </Box>
      )}
    </Stack>
  );
}
